/**
 * musickit-bridge.js
 *
 * Wraps MusicKit JS v3 and exposes a clean, Promise-based API for:
 *  - Authorization (developer token + user token)
 *  - Fetching library playlists and their tracks
 *  - Searching the Apple Music catalog
 *  - Controlling playback (play, pause, skip, seek)
 *  - Emitting playback-state and now-playing events
 *
 * MusicKit JS reference:
 *   https://developer.apple.com/documentation/musickitjs
 */

export class MusicKitBridge extends EventTarget {
  /**
   * @param {string} developerToken  - JWT signed with your MusicKit private key
   *                                   See README.md § "Generating a Developer Token"
   */
  constructor(developerToken) {
    super();

    if (!developerToken || developerToken === 'YOUR_DEVELOPER_TOKEN') {
      console.error(
        '[MusicKitBridge] No developer token provided.\n' +
          'Add your token to src/config.js — see README.md for instructions.'
      );
    }

    this.developerToken = developerToken;
    this.music = null; // MusicKit instance, set after configure()
    this._seekingManually = false;
  }

  // ─────────────────────────────────────────────────────────────
  // Initialisation
  // ─────────────────────────────────────────────────────────────

  /**
   * Configure MusicKit and wire up event listeners.
   * Must be called once after the `musickitloaded` window event fires.
   */
  async init() {
    await MusicKit.configure({
      developerToken: this.developerToken,
      app: {
        name: 'Webamp × Apple Music',
        build: '1.0.0',
        icon: 'https://yourdomain.vercel.app/webamp-apple.png',
      },
    });

    this.music = MusicKit.getInstance();
    this._bindMusicKitEvents();
    console.log('[MusicKitBridge] Initialised. Ready to authorise.');
  }

  /** Trigger the Apple Music sign-in popup. Returns the user token string. */
  async authorize() {
    const userToken = await this.music.authorize();
    this.dispatchEvent(new CustomEvent('authorized', { detail: { userToken } }));
    return userToken;
  }

  get isAuthorized() {
    return this.music?.isAuthorized ?? false;
  }

  /** Sign out of Apple Music and revoke the local session. */
  async disconnect() {
    try {
      if (this.music?.isAuthorized) {
        await this.music.unauthorize();
      }
    } catch (err) {
      console.warn('[MusicKitBridge] unauthorize error (ignored):', err);
    }
    this.dispatchEvent(new CustomEvent('disconnected'));
  }

  // ─────────────────────────────────────────────────────────────
  // Library — Playlists
  // ─────────────────────────────────────────────────────────────

  /**
   * Fetch all playlists from the user's Apple Music library.
   * @returns {Promise<Array>}  Array of Apple Music playlist resource objects
   */
  async getLibraryPlaylists() {
    const playlists = [];
    let offset = 0;
    const limit = 100;

    // MusicKit JS v3 — use music.api.music() with REST paths
    while (true) {
      const response = await this.music.api.music(
        `/v1/me/library/playlists?limit=${limit}&offset=${offset}`
      );

      const data = response?.data?.data ?? [];
      if (!data.length) break;

      playlists.push(...data);
      if (data.length < limit) break;
      offset += limit;
    }

    return playlists;
  }

  /**
   * Fetch recently played playlists (up to 10).
   * Returns only library-playlist and catalog playlist resources.
   * @returns {Promise<Array>}
   */
  async getRecentPlaylists() {
    const response = await this.music.api.music('/v1/me/recent/played?limit=10');
    const items = response?.data?.data ?? [];
    return items.filter(
      (item) => item.type === 'library-playlists' || item.type === 'playlists'
    );
  }

  /**
   * Fetch all tracks for a playlist — works for both library and catalog playlists.
   * @param {string} playlistId   - Playlist ID (e.g. "p.xxxxxxxx" or "pl.xxxxxxxx")
   * @param {string} [type]       - Resource type: 'library-playlists' (default) or 'playlists'
   * @returns {Promise<Array>}    Array of Apple Music song resource objects
   */
  async getPlaylistTracks(playlistId, type = 'library-playlists') {
    const limit = 100;

    // Apple Music ID prefixes: library playlists start with "p.", catalog with "pl."
    // The type field from recently-played can sometimes be mismatched with the actual ID,
    // so we also check the ID prefix and try the other endpoint as a fallback.
    const idIsCatalog = playlistId.startsWith('pl.');
    const typeIsCatalog = type === 'playlists';

    // Prefer ID-based detection when it disagrees with type field
    const primaryIsCatalog = idIsCatalog || typeIsCatalog;

    const fetchCatalogTracks = async () => {
      const storefront = this.music.storefrontId
        || this.music.storefrontCountryCode
        || await this.fetchStorefront();
      const tracks = [];
      let offset = 0;
      while (true) {
        const response = await this.music.api.music(
          `/v1/catalog/${storefront}/playlists/${playlistId}/tracks?limit=${limit}&offset=${offset}`
        );
        const data = response?.data?.data ?? [];
        if (!data.length) break;
        tracks.push(...data);
        if (data.length < limit) break;
        offset += limit;
      }
      return tracks;
    };

    const fetchLibraryTracks = async () => {
      const tracks = [];
      let offset = 0;
      while (true) {
        const response = await this.music.api.music(
          `/v1/me/library/playlists/${playlistId}/tracks?limit=${limit}&offset=${offset}`
        );
        const data = response?.data?.data ?? [];
        if (!data.length) break;
        tracks.push(...data);
        if (data.length < limit) break;
        offset += limit;
      }
      return tracks;
    };

    // Try primary endpoint, fall back to the other if it returns nothing or throws
    try {
      const primary = primaryIsCatalog ? await fetchCatalogTracks() : await fetchLibraryTracks();
      if (primary.length > 0) return primary;

      // Primary returned empty — try the other endpoint (some recently-played playlists
      // have mismatched type/ID so their tracks live at the alternate path)
      console.warn(`[MusicKitBridge] getPlaylistTracks: primary endpoint returned 0 tracks for ${playlistId} (type=${type}), trying fallback`);
      const fallback = primaryIsCatalog ? await fetchLibraryTracks() : await fetchCatalogTracks();
      return fallback;
    } catch (primaryErr) {
      console.warn(`[MusicKitBridge] getPlaylistTracks: primary endpoint failed for ${playlistId} (type=${type}):`, primaryErr);
      // Try the alternate endpoint
      try {
        const fallback = primaryIsCatalog ? await fetchLibraryTracks() : await fetchCatalogTracks();
        return fallback;
      } catch (fallbackErr) {
        console.error(`[MusicKitBridge] getPlaylistTracks: both endpoints failed for ${playlistId}`, fallbackErr);
        throw fallbackErr;
      }
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Catalog — Search
  // ─────────────────────────────────────────────────────────────

  /**
   * Search the Apple Music catalog.
   * @param {string} query
   * @returns {Promise<{ songs: Array, albums: Array, artists: Array }>}
   */
  async searchCatalog(query) {
    if (!query.trim()) return { songs: [], albums: [], artists: [] };

    // Storefront is needed for catalog search — fall back to 'us'
    const storefront = this.music.storefrontId
      || this.music.storefrontCountryCode
      || 'us';

    const term = encodeURIComponent(query.trim());
    const response = await this.music.api.music(
      `/v1/catalog/${storefront}/search?term=${term}&types=songs,albums,artists&limit=25`
    );

    const results = response?.data?.results ?? {};
    return {
      songs: results.songs?.data ?? [],
      albums: results.albums?.data ?? [],
      artists: results.artists?.data ?? [],
    };
  }

  /**
   * Fetch the user's storefront country code (e.g. "us", "gb").
   * Useful if music.storefrontId is not populated synchronously.
   */
  async fetchStorefront() {
    const response = await this.music.api.music('/v1/me/storefront');
    return response?.data?.data?.[0]?.id ?? 'us';
  }

  // ─────────────────────────────────────────────────────────────
  // Playback
  // ─────────────────────────────────────────────────────────────

  /**
   * Load a library playlist into the MusicKit queue only.
   * Callers are responsible for starting playback (via audio.play() proxy
   * or explicit mk.music.play()) to avoid double-play race conditions.
   * @param {string} playlistId
   */
  async playLibraryPlaylist(playlistId) {
    await this.music.setQueue({ playlist: playlistId });
    // Do NOT call play() here — the Webamp audio proxy fires play()
    // automatically when Webamp reacts to setTracksToPlay().
  }

  /**
   * Load an array of catalog song IDs into the queue and begin playback.
   * @param {string[]} songIds  Catalog song IDs
   * @param {number}   startIndex  Index to start playback at
   */
  async playCatalogSongs(songIds, startIndex = 0) {
    // Use startWith instead of changeToMediaAtIndex — changeToMediaAtIndex
    // internally calls play() which throws if already in loading/playing state.
    await this.music.setQueue({ songs: songIds, startWith: startIndex });
    await this._safePlay();
  }

  /** Play a single catalog song by ID */
  async playCatalogSong(songId) {
    await this.music.setQueue({ song: songId });
    await this._safePlay();
  }

  /**
   * Pause if currently playing/loading, then call play().
   * Centralises the "MusicKit requires stop/pause before play()" guard.
   */
  async _safePlay() {
    const { PlaybackStates } = MusicKit;
    const s = this.music.playbackState;
    if (
      s === PlaybackStates.playing ||
      s === PlaybackStates.loading ||
      s === PlaybackStates.waiting
    ) {
      await this.music.pause().catch(() => {});
    }
    await this.music.play();
  }

  async togglePlayPause() {
    if (this.isPlaying) {
      await this.music.pause();
    } else {
      await this.music.play();
    }
  }

  async nextTrack() {
    await this.music.skipToNextItem();
  }

  async previousTrack() {
    await this.music.skipToPreviousItem();
  }

  /**
   * Seek to a position in the current track.
   * @param {number} seconds
   */
  async seekTo(seconds) {
    this._seekingManually = true;
    await this.music.seekToTime(seconds);
    this._seekingManually = false;
  }

  /** @returns {boolean} */
  get isPlaying() {
    return (
      this.music?.playbackState === MusicKit.PlaybackStates.playing
    );
  }

  /** @returns {number} seconds */
  get currentTime() {
    return this.music?.currentPlaybackTime ?? 0;
  }

  /** @returns {number} seconds */
  get duration() {
    return this.music?.currentPlaybackDuration ?? 0;
  }

  /** @returns {object|null} The current MusicKit MediaItem */
  get nowPlayingItem() {
    return this.music?.nowPlayingItem ?? null;
  }

  // ─────────────────────────────────────────────────────────────
  // Bridge interface — shared with SpotifyBridge so WebampController
  // can drive either service without knowing which is active.
  // ─────────────────────────────────────────────────────────────

  /** Normalised PlaybackStates enum (mirrors SpotifyBridge.PlaybackStates shape). */
  get PlaybackStates() { return MusicKit.PlaybackStates; }

  /** Current playback state value. */
  get playbackState() { return this.music?.playbackState; }

  /** Index of the currently playing item in the MusicKit queue. */
  get nowPlayingItemIndex() { return this.music?.nowPlayingItemIndex ?? 0; }

  async skipToNextItem() {
    return this.music.skipToNextItem();
  }

  async skipToPreviousItem() {
    return this.music.skipToPreviousItem();
  }

  async stop() {
    return this.music.stop().catch(() => {});
  }

  async play() {
    return this.music.play();
  }

  async pause() {
    return this.music.pause().catch(() => {});
  }

  /**
   * Load a playlist and start at a specific index.
   * Normalised signature shared with SpotifyBridge.
   * @param {string} playlistId
   * @param {number} startIndex
   */
  async setQueue(playlistId, startIndex = 0) {
    return this.music.setQueue({ playlist: playlistId, startWith: startIndex });
  }

  /** Set playback volume. @param {number} fraction 0–1 */
  setVolume(fraction) {
    if (this.music) this.music.volume = fraction;
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers — convert Apple Music resources to Webamp track objects
  // ─────────────────────────────────────────────────────────────

  /**
   * Convert an Apple Music library-song or catalog-song resource into
   * the shape Webamp expects.
   *
   * NOTE: Webamp will be loaded with these as *display-only* entries
   * (the url points to the silent.mp3 placeholder). Actual audio
   * is handled by MusicKit. See webamp-controller.js for how we
   * wire playback events between the two.
   *
   * @param {object} resource  Apple Music song resource
   * @returns {object}         Webamp track object
   */
  static toWebampTrack(resource) {
    const attr = resource.attributes ?? {};
    const artUrl = attr.artwork?.url
      ? attr.artwork.url
          .replace('{w}', '200')
          .replace('{h}', '200')
      : null;

    return {
      // Webamp needs a URL; we give it a silent file so it doesn't error.
      // Actual playback is driven by MusicKit.
      url: '/silent.mp3',
      metaData: {
        title: attr.name ?? 'Unknown Title',
        artist: attr.artistName ?? 'Unknown Artist',
        album: attr.albumName ?? '',
        ...(artUrl ? { albumArtUrl: artUrl } : {}),
      },
      duration: attr.durationInMillis
        ? Math.round(attr.durationInMillis / 1000)
        : undefined,
      // Store the Apple Music ID so we can look it up on play
      _appleMusicId: resource.id,
      _appleMusicType: resource.type, // 'library-songs' | 'songs'
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Internal — MusicKit event binding
  // ─────────────────────────────────────────────────────────────

  _bindMusicKitEvents() {
    const { Events, PlaybackStates } = MusicKit;

    // Now Playing item changed → emit 'trackchange'
    this.music.addEventListener(
      Events.nowPlayingItemDidChange,
      (event) => {
        this.dispatchEvent(
          new CustomEvent('trackchange', { detail: event.item })
        );
      }
    );

    // Playback state changed → emit 'playstate'
    this.music.addEventListener(
      Events.playbackStateDidChange,
      (event) => {
        this.dispatchEvent(
          new CustomEvent('playstate', {
            detail: {
              state: event.state,
              isPlaying: event.state === PlaybackStates.playing,
            },
          })
        );
      }
    );

    // Playback time changed → emit 'timeupdate'
    this.music.addEventListener(
      Events.playbackTimeDidChange,
      () => {
        if (this._seekingManually) return;
        this.dispatchEvent(
          new CustomEvent('timeupdate', {
            detail: {
              currentTime: this.currentTime,
              duration: this.duration,
            },
          })
        );
      }
    );

    // Queue changed → emit 'queuechange'
    this.music.addEventListener(
      Events.queueItemsDidChange,
      (event) => {
        this.dispatchEvent(
          new CustomEvent('queuechange', { detail: event })
        );
      }
    );
  }
}
